/**
 * useRDACache Hook
 *
 * Fetches all RDA (Responsive Display Ad) headlines for a campaign on page load.
 * This data is shared between CampaignElementsList and AssetManagement components
 * to ensure consistent display of RDA headlines.
 *
 * Uses React Query for caching - results persist when navigating away and back.
 * Cache is invalidated when RDAs are added/removed via queryClient.invalidateQueries.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { getRDA, getLinkedImagesByEntity } from '@/services/api/assets';
import type { Campaign } from '@/types/campaign';
import type { RDACacheEntry, RDACacheImageAsset } from '@/components/features/AssetManagement/types';

// Re-export for convenience
export type { RDACacheEntry };

export interface UseRDACacheResult {
  rdaCache: Record<string, RDACacheEntry>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Extract all RDA ad IDs from a campaign's ad groups (excludes REMOVED ads)
 */
function extractRDAIds(campaign: Campaign): Array<{ adGroupId: string; adId: string }> {
  const rdaIds: Array<{ adGroupId: string; adId: string }> = [];

  const adGroups = campaign.ads_group_list || [];
  for (const adGroup of adGroups) {
    const adGroupId = adGroup.ad_group_id || adGroup.ads_group_id || '';
    const ads = adGroup.ads_list || adGroup.ads || [];

    for (const ad of ads) {
      if (ad.ad_type === 'RESPONSIVE_DISPLAY_AD' && ad.status !== 'REMOVED') {
        rdaIds.push({ adGroupId, adId: ad.ad_id });
      }
    }
  }

  return rdaIds;
}

/**
 * Fetch all RDA data for a campaign, including thumbnail URLs from linked images
 */
async function fetchAllRDAs(
  memberUuid: string,
  customerId: string,
  rdaIds: Array<{ adGroupId: string; adId: string }>
): Promise<Record<string, RDACacheEntry>> {
  const cache: Record<string, RDACacheEntry> = {};

  // Fetch all RDAs in parallel (both RDA data and linked images for thumbnails)
  const fetchPromises = rdaIds.map(async ({ adGroupId, adId }) => {
    try {
      // Fetch RDA data and linked images in parallel
      const [rdaResponse, linkedImagesResponse] = await Promise.all([
        getRDA({
          member_uuid: memberUuid,
          ad_group_id: adGroupId,
          ad_id: adId,
          customer_id: customerId,
        }),
        getLinkedImagesByEntity({
          member_uuid: memberUuid,
          entity_type: 'ad',
          entity_id: adId,
          customer_id: customerId,
        }),
      ]);

      if (rdaResponse.success) {
        // Build thumbnail map from linked images
        const thumbnailMap: Record<string, string> = {};
        if (linkedImagesResponse.success && linkedImagesResponse.images) {
          for (const img of linkedImagesResponse.images) {
            if (img.google_ads_asset_id && img.thumbnail_url) {
              thumbnailMap[img.google_ads_asset_id] = img.thumbnail_url;
            }
          }
        }

        // Helper to merge thumbnail URLs into image assets
        const mergeUrls = (assets: RDACacheImageAsset[] | undefined): RDACacheImageAsset[] => {
          if (!assets) return [];
          return assets.map((asset) => ({
            ...asset,
            url: thumbnailMap[asset.asset_id] || asset.url,
          }));
        };

        return {
          adId,
          data: {
            headlines: rdaResponse.headlines || [],
            long_headline: rdaResponse.long_headline,
            business_name: rdaResponse.business_name,
            descriptions: rdaResponse.descriptions,
            final_urls: rdaResponse.final_urls,
            // Include image assets with thumbnail URLs merged in
            marketing_images: mergeUrls(rdaResponse.marketing_images),
            square_marketing_images: mergeUrls(rdaResponse.square_marketing_images),
            logo_images: mergeUrls(rdaResponse.logo_images),
            landscape_logo_images: mergeUrls(rdaResponse.landscape_logo_images),
            // Also store the thumbnail map for RDAEditor
            thumbnailMap,
          },
        };
      }
    } catch (err) {
      // Log but don't fail the entire batch for one RDA
      console.warn(`Failed to fetch RDA ${adId}:`, err);
    }
    return null;
  });

  const results = await Promise.all(fetchPromises);

  for (const result of results) {
    if (result) {
      cache[result.adId] = result.data;
    }
  }

  return cache;
}

/**
 * Hook to fetch and cache all RDA headlines for a campaign.
 * Uses React Query for persistent caching across navigation.
 */
export function useRDACache(campaign: Campaign | null | undefined): UseRDACacheResult {
  const { memberUuid } = useAuth();

  // Extract all RDA IDs from the campaign
  const rdaIds = useMemo(() => {
    if (!campaign) return [];
    return extractRDAIds(campaign);
  }, [campaign]);

  // Create a stable key based on RDA IDs for cache invalidation
  const rdaIdsKey = useMemo(() => {
    return rdaIds.map(r => r.adId).sort().join(',');
  }, [rdaIds]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['rda-cache', campaign?.campaign_id, rdaIdsKey],
    queryFn: () => fetchAllRDAs(
      memberUuid!,
      campaign!.google_ads_account_id!,
      rdaIds
    ),
    enabled: !!memberUuid && !!campaign?.google_ads_account_id && rdaIds.length > 0,
    staleTime: Infinity, // Don't refetch automatically - only on invalidation
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  return {
    rdaCache: data ?? {},
    isLoading,
    error: error as Error | null,
  };
}

export default useRDACache;
